import { useAutoAnimate } from "@formkit/auto-animate/react";
import NotAvailableIcon from "@uui/MessageXSquare";
import { useParams } from "react-router";

import Typography from "@mui/material/Typography";

import DailyReadPlayer from "@app/analytics/components/DailyReadPlayer";
import useDailyRead from "@app/analytics/hooks/recordings/useDailyRead";
import useAuth from "@app/common/auth/useAuth";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Button from "@rui/Button";
import { EmptyStateIcon } from "@rui/EmptyStateContainer";
import Link from "@rui/Link";
import LogomarkWithGradient from "@rui/LogomarkWithGradient";
import Stack, { Column, Row } from "@rui/Stack";

import {
  AddReadButton,
  Card,
  Container,
  EmptyText,
  Header,
  Loading,
  MessageContainer,
  TranscriptBlock,
  TranscriptScrollContainer,
} from "./styles";

const ErrorState = () => (
  <Stack variant="center" padding={1}>
    <Stack variant="center" spacing={1} maxWidth="400px">
      <EmptyStateIcon Icon={NotAvailableIcon} padding={2} />
      <Typography variant="bodyPrimaryMed">Not available</Typography>
      <EmptyText>
        The Daily Read you are trying to view cannot be accessed at this time.
        Please try again later or reach out to{" "}
        <Link href={LINKS.EMAIL.SUPPORT} fontWeight={400}>
          support@read.ai
        </Link>{" "}
        if the problem persists.
      </EmptyText>
    </Stack>
  </Stack>
);

const NoDailyRead = () => (
  <Stack variant="center" padding={1} paddingBottom={2}>
    <Stack variant="center" spacing={1} maxWidth="400px">
      <EmptyStateIcon Icon={NotAvailableIcon} padding={2} />
      <Typography variant="bodyPrimaryMed">No Daily Read</Typography>
      <Column variant="center" spacing={2}>
        <EmptyText>
          You didn't have enough meeting data to generate a Daily Read today 🙁{" "}
          <Link to={ROUTES.CALENDAR} underline>
            Add Read
          </Link>{" "}
          to more meetings and check back tomorrow.
        </EmptyText>
        <EmptyText>
          In the meantime, check out your{" "}
          <Link to={ROUTES.FOR_YOU} underline>
            For You
          </Link>{" "}
          page!
        </EmptyText>
      </Column>
    </Stack>
  </Stack>
);

const DailyRead = () => {
  usePageTitle("Daily Read");

  const { user } = useAuth();
  const [cardRef] = useAutoAnimate();

  const { id } = useParams();

  const { isLoading, metadata } = useDailyRead(id);

  const isForCurrentUser = !id;

  const route = user ? ROUTES.LANDING_PAGE : ROUTES.SIGN_UP;

  return (
    <Container variant="center">
      <Card ref={cardRef}>
        <Header variant="apart" spacing={1}>
          <Link href={route}>
            <Row variant="left" spacing={1}>
              <LogomarkWithGradient
                sx={{ height: "30px", width: "30px", padding: "4px" }}
              />
              <Typography
                variant="h3"
                fontWeight={500}
                color="shades.neutral.800"
              >
                Read
              </Typography>
            </Row>
          </Link>
          {user ? (
            <Button sx={{ height: "40px" }} component={Link} href={route}>
              <span className="daily-read__button--extra-text">
                Go to&nbsp;
              </span>
              Dashboard
            </Button>
          ) : (
            <Button sx={{ height: "40px" }} component={Link} href={route}>
              Sign up
              <span className="daily-read__button--extra-text">
                &nbsp;for free
              </span>
            </Button>
          )}
        </Header>
        {isLoading ? (
          <Loading />
        ) : metadata ? (
          <Stack spacing={2} maxHeight="100%" sx={{ overflow: "hidden" }}>
            {metadata.isStale && (
              <MessageContainer spacing={1}>
                <Typography variant="bodySecondaryReg">
                  You didn't have enough meeting data to generate a Daily Read
                  today 🙁{" "}
                  <Link to={ROUTES.CALENDAR} underline>
                    Add Read
                  </Link>{" "}
                  to more meetings and check back tomorrow.
                </Typography>
                <AddReadButton />
              </MessageContainer>
            )}
            <DailyReadPlayer
              metadata={metadata}
              forCurrentUser={isForCurrentUser}
              // disable the internal stale UI in the player, as that's handled above
              isStale={false}
              shareable={isForCurrentUser}
            />
            <TranscriptBlock>
              <TranscriptScrollContainer spacing={2}>
                {metadata.transcript.map((p) => (
                  <Typography key={p} variant="bodySecondaryReg">
                    {p}
                  </Typography>
                ))}
              </TranscriptScrollContainer>
            </TranscriptBlock>
          </Stack>
        ) : isForCurrentUser ? (
          <NoDailyRead />
        ) : (
          <ErrorState />
        )}
      </Card>
    </Container>
  );
};

export default DailyRead;
